import { useAuthRegister } from '@/api/gen-new/auth/auth';
import { groupInviteGetInviteDetails } from '@/api/gen-new/group/group';
import { getStoredAttribution } from '@/analytics/attribution';
import { getGaClientId, trackSignUp, trackStartTrial } from '@/analytics/gtag';
import { prefetchSignedInShell } from '@/app/prefetchSignedInShell';
import AuthFormLayout from '@/auth/components/AuthFormLayout';
import GoogleLoginButton from '@/auth/components/GoogleLoginButton';
import { routeAfterAuthenticated } from '@/auth/constants/authSettings';
import { getSession } from '@/auth/AuthSession';
import { startSessionFromTokens } from '@/auth/AuthUtils';
import requirements, { colors, getStrength, yupSchema } from '@/auth/constants/passwordRequirements';
import useAuth from '@/auth/useAuth';
import defaultNotifications from '@/components/functions/defaultNotifications';
import RequirementDisplay from '@/components/displays/RequirementDisplay';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Button, Group, PasswordInput, Popover, Progress, Text, TextInput } from '@mantine/core';
import { IconAlertCircle, IconUsers } from '@tabler/icons-react';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import * as yup from 'yup';

const schema = yup.object({
	email: yup.string().email('errors.emailInvalid').required('errors.emailRequired'),
	name: yup.string().required('errors.nameRequired'),
	password: yupSchema,
	confirmPassword: yup
		.string()
		.required('errors.confirmNewPasswordRequired')
		.oneOf([yup.ref('password')], 'errors.confirmNewPasswordMustMatch'),
});

type FormData = yup.InferType<typeof schema>;

export default function RegisterPage() {
	const navigate = useNavigate();

	const location = useLocation();
	const searchParams = new URLSearchParams(location.search);
	const invitationCode = searchParams.get('code');
	const invitedEmail = searchParams.get('email');

	const { mutateAsync: postAuthRegister } = useAuthRegister();

	const { isAuthenticated } = useAuth();

	const [showRequirements, setShowRequirements] = useState(false);
	const [isConfirmFocused, setIsConfirmFocused] = useState(false);
	const [invitationDetails, setInvitationDetails] = useState<any>(null);
	const [invitationError, setInvitationError] = useState<string | null>(null);

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
		setValue: setFormValue,
		watch,
	} = useForm<FormData>({
		resolver: yupResolver(schema),
		defaultValues: invitedEmail ? { email: invitedEmail } : undefined,
	});

	// If invitedEmail is present OR we have invitation details with email, lock the email field
	const isEmailLocked = !!invitedEmail || !!invitationDetails?.email;

	const value = watch('password') ?? '';
	const confirmValue = watch('confirmPassword') ?? '';

	useEffect(() => {
		if (isAuthenticated()) {
			navigate(routeAfterAuthenticated);
		} else {
			prefetchSignedInShell();
		}
	}, [isAuthenticated, navigate]);

	// Fetch invitation details if invitation code is present
	useEffect(() => {
		if (invitationCode) {
			const fetchInvitationDetails = async () => {
				try {
					const details = await groupInviteGetInviteDetails(invitationCode);
					setInvitationDetails(details);
					setInvitationError(null); // Clear any previous invitation errors

					// If no email was provided in URL params but we have it from invitation, pre-fill it
					if (!invitedEmail && details.email) {
						setFormValue('email', details.email);
					}
				} catch (error) {
					console.error('Failed to fetch invitation details:', error);
					setInvitationError(t('login.notifications.invalidInvitation'));
				}
			};
			fetchInvitationDetails();
		}
	}, [invitationCode, invitedEmail, setFormValue]);

	const onSubmit = async (data: FormData) => {
		// Clear any previous invitation errors
		setInvitationError(null);

		try {
			// GA4 attribution captured at signup: the client_id ties the eventual server-side
			// purchase back to this visitor, and the stored first-touch UTM/referrer let paid
			// conversions trace to the originating campaign. Best-effort — undefined when GA is
			// blocked/unconfigured or on native.
			const gaClientId = await getGaClientId();
			const attribution = getStoredAttribution();

			const registrationData: any = {
				email: data.email,
				name: data.name,
				password: data.password,
				gaClientId,
				...attribution,
			};

			// Include invitation code if present
			if (invitationCode) {
				registrationData.invitationCode = invitationCode;
			}

			const tokens = await postAuthRegister({
				data: registrationData,
			});

			// Funnel events (web-only no-ops otherwise). sign_up on every new account; start_trial
			// only for normal registration — invited users join an existing space rather than
			// starting their own free-plan trial.
			trackSignUp('password');
			if (!invitationCode) {
				trackStartTrial({ plan: 'free' });
			}

			// Both paths start a session, so a new user lands in the product unverified.
			await startSessionFromTokens(tokens, { loginMethod: 'email', email: data.email });

			const session = getSession();
			navigate(session?.userGroupId ? routeAfterAuthenticated : '/no-space');

			// biome-ignore lint/suspicious/noExplicitAny: FIXME
		} catch (err: any) {
			if ('response' in err) {
				const { response } = err;
				if ('status' in response) {
					if (response.status === 403) {
						defaultNotifications.showError({
							message: t('register.notifications.cannotRegister'),
						});
						return;
					}
					if (response.status === 422) {
						// Handle invitation-related errors - show in Alert component
						const errorData = response.data;
						if (errorData && errorData.message) {
							let errorMessage = t('login.notifications.invalidInvitation');

							// Map specific invitation errors
							if (errorData.message === 'invite.not.found') {
								errorMessage = t('invitation.notFound');
							} else if (errorData.message === 'invite.expired') {
								errorMessage = t('invitation.expired');
							}

							setInvitationError(errorMessage);
							return;
						}
					}
				}
			}
			defaultNotifications.showError({
				message: t('register.notifications.invalidCredentials'),
			});
		}
	};

	const handleOnChange = (password: string, confirmPassword: string) => {
		// propagate to form state so yup can validate and errors show translated
		setFormValue('password', password, { shouldValidate: true });
		setFormValue('confirmPassword', confirmPassword, { shouldValidate: true });
	};

	const passwordStrength = getStrength(value);
	const passwordColor =
		passwordStrength === 100 ? colors.Success : passwordStrength > 50 ? colors.Warning : colors.Error;

	const doesConfirmMatch = !!value?.length && !!confirmValue?.length && value === confirmValue;
	const confirmStrength = doesConfirmMatch ? 100 : 50;
	const confirmColor = doesConfirmMatch ? colors.Success : colors.Error;

	// Create invitation header for both success and error states
	const invitationHeader = invitationCode ? (
		invitationError ? (
			// Show invitation error
			<Alert icon={<IconAlertCircle size={16} />} title={t('login.invitationError')} color="red" variant="light">
				<Text size="sm">{invitationError}</Text>
			</Alert>
		) : invitationDetails ? (
			// Show successful invitation details
			<Alert icon={<IconUsers size={16} />} title={t('register.joinSpace')} color="blue" variant="light">
				<Text size="sm">
					{t('register.inviteMessage', {
						spaceName: invitationDetails.userGroup?.name || 'this space',
					})}
				</Text>
			</Alert>
		) : null
	) : null;

	return (
		<AuthFormLayout
			title={t('register.title')}
			header={invitationHeader}
			onSubmit={handleSubmit(onSubmit)}
			fields={
				<>
					<TextInput
						{...register('email')}
						label={t('register.email')}
						autoComplete="username"
						error={errors.email?.message && t(`register.${errors.email.message}`)}
						disabled={isEmailLocked}
					/>
					<TextInput
						{...register('name')}
						label={t('register.name')}
						autoComplete="name"
						error={errors.name?.message && t(`register.${errors.name.message}`)}
					/>
					<Popover opened={showRequirements} position="right" width="target" transitionProps={{ transition: 'pop' }}>
						<Popover.Target>
							<div onFocusCapture={() => setShowRequirements(true)} onBlurCapture={() => setShowRequirements(false)}>
								<PasswordInput
									{...register('password')}
									withAsterisk
									label={t('register.password')}
									autoComplete="new-password"
									value={value}
									onChange={event => handleOnChange(event.currentTarget.value, confirmValue)}
									error={errors.password && t(`register.${errors.password.message}`)}
								/>
							</div>
						</Popover.Target>
						<Popover.Dropdown>
							<Progress color={passwordColor} value={passwordStrength} size={5} mb="xs" />
							{requirements.map(req => (
								<RequirementDisplay key={req.key} label={t(`register.${req.key}`)} meets={req.re.test(value)} />
							))}
						</Popover.Dropdown>
					</Popover>
					<Popover opened={isConfirmFocused} position="right" width="target" transitionProps={{ transition: 'pop' }}>
						<Popover.Target>
							<div onFocusCapture={() => setIsConfirmFocused(true)} onBlurCapture={() => setIsConfirmFocused(false)}>
								<PasswordInput
									label={t('register.confirmPassword')}
									autoComplete="new-password"
									{...register('confirmPassword')}
									value={confirmValue}
									onChange={event => handleOnChange(value, event.currentTarget.value)}
									error={errors.confirmPassword && t(`register.${errors.confirmPassword.message}`)}
								/>
							</div>
						</Popover.Target>
						<Popover.Dropdown>
							<Progress color={confirmColor} value={confirmStrength} size={5} mb="xs" />
							<RequirementDisplay label={t('register.errors.confirmNewPasswordMustMatch')} meets={doesConfirmMatch} />
						</Popover.Dropdown>
					</Popover>
				</>
			}
			// auxLink={{
			//     text: t('login.forgotPassword'),
			//     to: '/forgot-password',
			// }}
			actionButton={
				<Button type="submit" aria-disabled={isSubmitting} disabled={isSubmitting} variant="filled">
					{t('register.register')}
				</Button>
			}
			altAction={{
				preText: t('register.loginText'),
				text: t('register.login'),
				to: '/login',
			}}
			footerDividerText={t('login.or')}
			footer={
				<Group justify="center">
					<GoogleLoginButton />
				</Group>
			}
		/>
	);
}
